/* eslint-disable @next/next/no-img-element */
import { CheckIcon, ChevronLeftIcon } from '@heroicons/react/solid';
import clsx from 'clsx';
import get from 'lodash/get';
import { observer } from 'mobx-react';
import Head from 'next/head';
import Link from 'next/link';
import { LegacyRef, forwardRef, useEffect, useRef, useState } from 'react';
import { BsFillBookmarkFill, BsHospital } from 'react-icons/bs';
import { FiShare2 } from 'react-icons/fi';
import { RiCameraLine } from 'react-icons/ri';
import { MapRef, NavigationControl } from 'react-map-gl';

import CustomMap, { CustomMarker } from 'components/elements/customMap';
import * as Icons from 'components/elements/icons';
import { PdpService } from 'lib/apis/services/PdpSingle';
import useOwnRouter from 'lib/hooks/useOwnRouter';
import { useOwnTranslate } from 'lib/hooks/useOwnTranslate';
import { Pricing } from '../../../../lib/apis/services/PdpSingle/type';
import HelperTranslate from '../../../../lib/utils/helpers/helperTranslate';
import TrueCheck from '../../../elements/TrueCheck';
import { singleDynamicModle } from '../constant/single';
import { useContactInfo } from '../hooks/useContanctInfo';
import GallerySlider from './gallerySlider';
import MobileContactInfo from './mobileContactInfo';
import PropertyPricing from './propertyPricing';
import Maybe from '../../../elements/maybe';
import NearbyItem from './NearbyItem';
import { GiBusStop, GiSchoolBag, GiShop } from 'react-icons/gi';
import { BiClinic, BiSolidClinic } from 'react-icons/bi';
import { MdOutlinePark } from 'react-icons/md';
import { IoIosSubway } from 'react-icons/io';

interface PropertyProps {
  refererUrl?: string;
}

interface NearbyAreas {
  distance: number;
  name: string;
  poiType: 'MALL' | 'HOSPITAL' | 'CLINIC' | 'PARK' | 'SCHOOL' | 'SUBWAY' | 'BUS_STOP';
}

function PropertyInfo(
  _props: PropertyProps,
  ref: LegacyRef<HTMLParagraphElement>,
) {
  const { shareModal, saveListing, favStatus, makeFav } = useContactInfo();
  const [descriptionOpen, setDescriptionOpen] = useState(false);
  const [nearbyAreaOpen, setNearbyAreaOpen] = useState(false);
  const map = useRef<MapRef>(null);
  const [carouselIndex, setCarouselIndex] = useState(1);
  const router = useOwnRouter();
  const { t } = useOwnTranslate('plp.propertyInfo');
  const { data } = PdpService.usePdpSingleQuery(
    router.router.query.id as string,
  );
  const pageDataListing = data?.data.listing;
  const isBuy = pageDataListing!.listingType;
  const dynamicModel = singleDynamicModle[isBuy === 'BUY' ? 'buy' : 'rent'];


  const poiList: NearbyAreas[] | undefined = data?.data.pois;
  let malls: NearbyAreas[] = [],
    hospitals: NearbyAreas[] = [],
    clinics: NearbyAreas[] = [],
    parks: NearbyAreas[] = [],
    schools: NearbyAreas[] = [],
    subways: NearbyAreas[] = [],
    busStops: NearbyAreas[] = [];


  if (poiList) {
    poiList.forEach((item) => {
      if (item && typeof item.poiType === 'string') {
        switch (item.poiType) {
          case 'MALL':
            malls.push(item);
            break;
          case 'HOSPITAL':
            hospitals.push(item);
            break;
          case 'CLINIC':
            clinics.push(item);
            break;
          case 'PARK':
            parks.push(item);
            break;
          case 'SCHOOL':
            schools.push(item);
            break;
          case 'SUBWAY':
            subways.push(item);
            break;
          default:
            busStops.push(item);
        }
      }
    });
  }

  const countLinesInString = (str: string) => {
    return str.split('\n').length;
  };

  useEffect(() => {
    return () => {
      setCarouselIndex(1);
    };
  }, [JSON.stringify(data)]);

  const pricing: Pricing = {
    price: pageDataListing?.priceOrDeposit,
    deposit: pageDataListing?.priceOrDeposit,
    rent: pageDataListing?.unitPriceOrRent,
    unitPrice: pageDataListing?.unitPriceOrRent,
    currency: '',
    agreedPrice: pageDataListing?.agreedPrice,
    depositOnly: pageDataListing?.depositOnly,
    capRatePercentage: 0,
    listingType: pageDataListing!.listingType,
  };

  return (
    <>
      <Head>
        <title>
          {get(pageDataListing, dynamicModel.title.path, dynamicModel.title.default)}
        </title>

        <meta
          name='description'
          content={
            pageDataListing?.description
              ? pageDataListing.description
                .replace(/\\<*.*?\\>/g, '') //ToDo: check this after published
                .replace(/\r?\n|\r/g, '')
              : 'کیلید'
          }
        />
      </Head>
      <div className='right-side-details flex flex-col flex-1 w-full md:w-[42%]'>
        <div className='relative -mx-4 md:mx-0'>
          {/*gallery section*/}
          <TrueCheck isShow={pageDataListing!.trueCheck} />
          {pageDataListing!.expired && (
            <>
              <p className='absolute z-10 text-white -translate-x-1/2 -translate-y-1/3 left-1/2 top-1/2'>
                بایگانی شده
              </p>
              <div
                className='absolute z-10 mix-blend-multiply bg-gray-500 w-full md:f-[220] left-0 right-0 top-0 bottom-0 flex justify-center items-center' />
            </>
          )}
          {dynamicModel.media.pictures.type === 'map' && (
            pageDataListing && (
              <GallerySlider
                pdpId={pageDataListing.id.toString()}
                key={pageDataListing.id}
                images={pageDataListing.pictures || []}
                onSlideChange={(swiper) => {
                  setCarouselIndex(swiper.activeIndex + 1);
                }}
              />
            ))}
          {/*number of pictures show in mobile version*/}
          <div
            className='absolute z-10 flex items-center justify-center p-1 space-x-2 bg-blue-900 bg-opacity-50 rtl:space-x-reverse md:hidden bottom-3 left-3'>
            <RiCameraLine className='w-4 h-4 text-white' />
            <span className='text-white'>
              {carouselIndex} / {pageDataListing?.pictures.length}
            </span>
          </div>

          {/*property code show in mobile version*/}
          <div
            className='absolute md:hidden flex space-x-2 rtl:space-x-reverse p-1 bg-blue-900 bg-opacity-50 justify-center items-center top-3 left-3 max-w-[105px] leading-5 z-10'>
            <span className='text-white'>
              {t('listingId')}: {pageDataListing?.id}
            </span>
          </div>

          {/*share and favorites button show in mobile version*/}
          <div
            className='absolute z-10 flex items-center justify-center p-1 space-x-3 md:hidden bottom-3 right-3 rtl:space-x-reverse'>
            <button
              type='button'
              className='inline-flex items-center justify-center p-3 text-sm font-medium text-center bg-white rounded-md outline-none text-grey-700 ring-1 ring-grey-300 focus:text-primary-400 focus:ring-primary-400'
              onClick={() => {
                shareModal.openModal();
              }}
            >
              <FiShare2 className='w-5 h-5' />
              <span className='sr-only'>
                {t('global.iconDescription', true)}
              </span>
            </button>
            <button
              type='button'
              className={clsx(
                'text-grey-700 bg-white ring-1 ring-grey-300 focus:text-primary-400 focus:ring-primary-400 outline-none font-medium rounded-md text-sm p-3 w-[44px] h-[44px] text-center inline-flex items-center justify-center',
                {
                  'text-grey-800': !favStatus && !makeFav.isLoading,
                  'text-grey-500 cursor-wait': !favStatus && makeFav.isLoading,
                  '!bg-primary-500 !text-white': favStatus,
                },
              )}
              onClick={saveListing}
            >
              <BsFillBookmarkFill className='w-4 h-4' />
              <span className='sr-only'>
                {t('global.iconDescription', true)}
              </span>
            </button>
          </div>
        </div>

        <div className='info'>
          {/* title, pricing, capRate(soon) */}
          <div className='flex flex-col pb-12 mt-8 prose border-b border-gray-200'>
            <div className='flex justify-between mb-5 space-x-2 rtl:space-x-reverse'>
              {/*property title*/}
              <h1 ref={ref} className='m-0 text-xl font-bold'>
                {get(pageDataListing, dynamicModel.title.path, dynamicModel.title.default)}
              </h1>
              {/* mobile ontactInfo */}
              <MobileContactInfo />
            </div>
            <div>
              {pageDataListing && <PropertyPricing pricing={pricing} />}
            </div>

            {/* location */}
            <p className='inline-flex mb-6'>
              <svg
                xmlns='http://www.w3.org/2000/svg'
                viewBox='0 0 25 25'
                fill='currentColor'
                className='w-6 h-6 mr-2 text-gray-500'
              >
                <path
                  fillRule='evenodd'
                  d='M11.54 22.351l.07.04.028.016a.76.76 0 00.723 0l.028-.015.071-.041a16.975 16.975 0 001.144-.742 19.58 19.58 0 002.683-2.282c1.944-1.99 3.963-4.98 3.963-8.827a8.25 8.25 0 00-16.5 0c0 3.846 2.02 6.837 3.963 8.827a19.58 19.58 0 002.682 2.282 16.975 16.975 0 001.145.742zM12 13.5a3 3 0 100-6 3 3 0 000 6z'
                  clipRule='evenodd'
                />
              </svg>
              {get(
                pageDataListing,
                dynamicModel.location.locationName.path,
                dynamicModel.location.locationName.default,
              )}
            </p>

            {/* attributes */}
            <div className='w-full'>
              <ul className='flex flex-wrap gap-2 p-0 m-0 list-none'>
                {dynamicModel.attributes.chips.map(
                  (chipsItem, index) => {
                    const value = get(pageDataListing, chipsItem.path, chipsItem.default);
                    return !!value && (
                      <li
                        key={`${chipsItem.path}-ch-${index}`}
                        className='px-2 py-1 text-sm rounded-lg bg-grey-100'
                      >
                        {chipsItem.prefix ? `${chipsItem.prefix} ` : ''}
                        {HelperTranslate(t, chipsItem.path, get(pageDataListing, chipsItem.path, chipsItem.default))}
                        {chipsItem.suffix ? ` ${chipsItem.suffix}` : ''}
                      </li>
                    );
                  },
                )}
              </ul>
            </div>
          </div>
          {/* features */}
          {pageDataListing?.features && (get(pageDataListing, dynamicModel.specifics.path, [])).length !== 0 &&
          <div className='flex flex-col max-w-full pb-12 prose text-gray-800 border-b border-gray-200'>
            <div>
              <h2>{t('features')}</h2>
            </div>
            <div className='justify-start w-full space-y-6 align-middle felx md:columns-3 columns-2'>
              {dynamicModel.specifics.type === 'map' &&
              (get(pageDataListing, dynamicModel.specifics.path, []))?.map((spec: string, index: number) => (
                <div
                  className='flex flex-row justify-start align-middle'
                  key={index + spec}
                >
                  <CheckIcon className='w-4 text-gray-800 ltr:mr-4' />

                  <span className='text-lg text-gray-800'>
                        {t(`enums.Features.${spec}`, true)}
                      </span>
                </div>
              ))}
            </div>
          </div>
          }
          {/* tags */}
          {pageDataListing?.tags && (get(pageDataListing, dynamicModel.tags.path, [])).length !== 0 &&
          <div className='flex flex-col max-w-full pb-12 prose text-gray-800 border-b border-gray-200'>
            <div>
              <h2>{t('tags')}</h2>
            </div>
            <div className='justify-start w-full space-y-6 align-middle felx md:columns-3 columns-2'>
              {dynamicModel.tags.type === 'map' &&
              (get(pageDataListing, dynamicModel.tags.path, []))?.map((tag: string, index: number) => (
                <div
                  className='flex flex-row justify-start align-middle'
                  key={index + tag}
                >
                  <CheckIcon className='w-4 text-gray-800 ltr:mr-4' />

                  <span className='text-lg text-gray-800'>
                        {t(`enums.Tags.${tag}`, true)}
                      </span>
                </div>
              ))}
            </div>
          </div>
          }
          {/* description */}
          <div className='flex flex-col max-w-full pb-12 prose text-gray-800 border-b border-gray-200'>
            <div>
              <h2>{t('global.description', true)}</h2>
            </div>
            <div
              className={clsx(
                'break-words text-justify whitespace-pre-line',
                { 'line-clamp-none': descriptionOpen },
                { 'line-clamp-3': !descriptionOpen },
              )}
              dangerouslySetInnerHTML={{
                __html: pageDataListing?.description || '',
              }}
            />
            {countLinesInString(pageDataListing?.description || '') > 3 && (
              <button
                className='btn btn-primary-link items-center !px-0 mt-8'
                onClick={() => setDescriptionOpen((state) => !state)}
              >
                {!descriptionOpen ? t('global.showMore', true) : t('global.showLess', true)}
                <Icons.Chevron direction={descriptionOpen ? 'up' : 'down'} />
              </button>
            )}
          </div>

          {/*nearby areas*/}
          <Maybe condition={!!poiList}>
            <div className='flex flex-col max-w-full pb-12 prose text-gray-800 border-b border-gray-200'>
              <div>
                <h2>مناطق نزدیک</h2>
              </div>
              <div
                className={clsx(
                  'break-words text-justify whitespace-pre-line',
                  { 'line-clamp-none': nearbyAreaOpen },
                  { 'line-clamp-2': !nearbyAreaOpen },
                )}
              >
                {malls.length > 0 && <NearbyItem nearbyList={malls} title='مراکز خرید' icon={<GiShop />} />}
                {hospitals.length > 0 &&
                <NearbyItem nearbyList={hospitals} title='بیمارستان ها' icon={<BsHospital />} />}
                {clinics.length > 0 && <NearbyItem nearbyList={clinics} title='درمانگاه ها' icon={<BiClinic />} />}
                {parks.length > 0 && <NearbyItem nearbyList={parks} title='بوستان ها' icon={<MdOutlinePark />} />}
                {schools.length > 0 && <NearbyItem nearbyList={schools} title='مدارس' icon={<GiSchoolBag />} />}
                {subways.length > 0 &&
                <NearbyItem nearbyList={subways} title='ایستگاه های مترو' icon={<IoIosSubway />} />}
                {busStops.length > 0 &&
                <NearbyItem nearbyList={busStops} title='ایستگاه های اتوبوس' icon={<GiBusStop />} />}
              </div>
              {poiList && poiList.length > 3 && (
                <button
                  className='btn btn-primary-link items-center !px-0 mt-8'
                  onClick={() => setNearbyAreaOpen((state) => !state)}
                >
                  {!nearbyAreaOpen ? t('global.showMore', true) : t('global.showLess', true)}
                  <Icons.Chevron direction={nearbyAreaOpen ? 'up' : 'down'} />
                </button>
              )}
            </div>
          </Maybe>


          {/* location */}
          <div className='flex flex-col max-w-full pb-12 mt-8 text-gray-800 border-b border-gray-200'>
            <div className='mb-4 prose'>
              <h2 className='mb-0'>{t('locationOnTheMap')}</h2>
              <p className='text-grey-500'>
                {get(
                  pageDataListing,
                  dynamicModel.location.locationName.path,
                  dynamicModel.location.locationName.default,
                )}
              </p>
            </div>
            {pageDataListing && pageDataListing.point?.coordinate[0] && pageDataListing.point?.coordinate[1] && (
              <div className='flex w-full'>
                <CustomMap
                  key={`${pageDataListing.point?.coordinate[0]}, ${pageDataListing.point?.coordinate[1]
                  }`}
                  ref={map}
                  scrollZoom={false}
                  initialViewState={{
                    longitude: pageDataListing.point?.coordinate[0],
                    latitude: pageDataListing.point?.coordinate[1],
                    zoom: 16,
                  }}
                  style={{ width: '100%', height: 400 }}
                >
                  <NavigationControl
                    position='bottom-right'
                    showCompass={false}
                  />
                  <CustomMarker
                    {...{
                      longitude: pageDataListing.point?.coordinate[0],
                      latitude: pageDataListing.point?.coordinate[1],
                    }}
                  />
                </CustomMap>
              </div>
            )}
          </div>

          <div
            className='inline-flex items-center justify-start w-full max-w-full py-2 space-x-4 overflow-x-auto text-gray-800 align-middle border-b border-gray-200 rtl:space-x-reverse snap-x md:overflow-x-hidden'>
            {data?.data?.breadCrumbs?.map((bread, index) => (
              <div
                className='flex items-center justify-center space-x-4 rtl:space-x-reverse'
                key={`p-bread-crumbs-${index}`}
              >
                <Link prefetch={false} href={bread.url}>
                  <a className='w-max'>{bread.title}</a>
                </Link>
                <ChevronLeftIcon className='w-5' />
              </div>
            ))}
            <span className='text-primary-700'>{pageDataListing?.id}</span>
          </div>
        </div>
      </div>
    </>
  );
}

export default observer(
  forwardRef<HTMLParagraphElement, PropertyProps>(PropertyInfo),
);
